import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shield, Save, RefreshCw, CheckCircle, AlertCircle, AlertTriangle, Info, Settings, Link2, Plus, X } from 'lucide-react';
import { suricataService, edgeService } from '../../../services';
import type { SuricataConfig } from '../../../services';
import { useTenantStore } from '../../../stores/tenantStore';
import { toast } from 'react-hot-toast';
import ServiceChainSettings from './ServiceChainSettings';
import FeatureDisabledBanner from '../../../components/common/FeatureDisabledBanner';
import { useSettingsStore } from '../../../stores/settingsStore';
import { usePermissions } from '../../../hooks/usePermissions';

interface SuricataSettingsProps {
  edgeId: string;
}

type TabId = 'settings' | 'service-chain';

interface DetectedNetwork {
  subnet: string;
  source: string;
}

const VALID_RULE_ACTIONS = ['alert', 'pass', 'drop', 'reject', 'rejectsrc', 'rejectdst', 'rejectboth'];

const validateCustomRules = (rules: string): { valid: boolean; error?: string } => {
  if (!rules.trim()) return { valid: true };

  const lines = rules.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line || line.startsWith('#')) continue;

    const action = line.split(/\s+/)[0]?.toLowerCase();
    if (!VALID_RULE_ACTIONS.includes(action)) {
      return { valid: false, error: `Line ${i + 1}: Must start with a valid action (${VALID_RULE_ACTIONS.join(', ')})` };
    }
    if (!line.includes('sid:')) {
      return { valid: false, error: `Line ${i + 1}: Missing required 'sid:' field` };
    }
  }
  return { valid: true };
};

const SuricataSettings = ({ edgeId }: SuricataSettingsProps) => {
  const [searchParams] = useSearchParams();
  const subtabParam = searchParams.get('subtab');
  const { isFeatureEnabled } = useSettingsStore();
  const { canUpdate } = usePermissions();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);

  const getInitialTab = (): TabId => {
    const validTabs: TabId[] = ['settings', 'service-chain'];
    if (subtabParam && validTabs.includes(subtabParam as TabId)) return subtabParam as TabId;
    return 'settings';
  };

  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab);
  const [config, setConfig] = useState<SuricataConfig>({
    edge_id: edgeId,
    enabled: false,
    mode: 'ips',
    threads: 2,
    af_xdp_mode: 'drv',
    busy_poll_enabled: true,
    detect_profile: 'medium',
    home_net: ['192.168.0.0/16', '10.0.0.0/8', '172.16.0.0/12'],
    rule_sources: ['et/open'],
    custom_rules: '',
    rule_update_enabled: true,
    alert_threshold: 'high',
    block_severity: 'critical',
    service_chain_enabled: true,
    service_chain_mode: 'tap-afxdp',
    traffic_selector: 'all',
    fallback_action: 'forward',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalConfig, setOriginalConfig] = useState<SuricataConfig | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Home network auto-detection
  const [detectedNetworks, setDetectedNetworks] = useState<DetectedNetwork[]>([]);
  const [customSubnet, setCustomSubnet] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchNetworks();
  }, [edgeId, selectedTenantId]);

  const defaultConfig: SuricataConfig = {
    edge_id: edgeId,
    enabled: false,
    mode: 'ips',
    threads: 2,
    af_xdp_mode: 'drv',
    busy_poll_enabled: true,
    detect_profile: 'medium',
    home_net: ['192.168.0.0/16', '10.0.0.0/8', '172.16.0.0/12'],
    rule_sources: ['et/open'],
    custom_rules: '',
    rule_update_enabled: true,
    alert_threshold: 'high',
    block_severity: 'critical',
    service_chain_enabled: true,
    service_chain_mode: 'tap-afxdp',
    traffic_selector: 'all',
    fallback_action: 'forward',
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await suricataService.getConfig(edgeId);
      if (response.config) {
        const mergedConfig = { ...defaultConfig, ...response.config };
        setConfig(mergedConfig);
        setOriginalConfig(mergedConfig);
      } else {
        setConfig(defaultConfig);
        setOriginalConfig(defaultConfig);
      }
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to fetch Suricata config:', error);
      setConfig(defaultConfig);
      setOriginalConfig(defaultConfig);
    } finally {
      setLoading(false);
    }
  };

  const fetchNetworks = async () => {
    try {
      const [interfacesRes, routesRes] = await Promise.all([
        edgeService.listLogicalInterfaces(edgeId).catch(() => null),
        edgeService.listStaticRoutes(edgeId).catch(() => null),
      ]);

      const networks: DetectedNetwork[] = [];

      // Extract subnets from logical interfaces
      if (interfacesRes?.interfaces) {
        for (const iface of interfacesRes.interfaces) {
          if (iface.ip_address && iface.subnet_mask) {
            const cidr = ipToCidr(iface.ip_address, iface.subnet_mask);
            if (cidr) {
              networks.push({
                subnet: cidr,
                source: `${iface.name || iface.logical_port_id} — ${cidr}`,
              });
            }
          }
        }
      }

      // Extract subnets from static routes
      if (routesRes?.routes) {
        for (const route of routesRes.routes) {
          if (route.destination) {
            const dest = route.destination.includes('/') ? route.destination : `${route.destination}/32`;
            networks.push({
              subnet: dest,
              source: `Static Route — ${dest}`,
            });
          }
        }
      }

      setDetectedNetworks(networks);
    } catch (error) {
      console.error('Failed to fetch networks for auto-detection:', error);
    }
  };

  const ipToCidr = (ip: string, mask: string): string | null => {
    try {
      const maskParts = mask.split('.').map(Number);
      if (maskParts.length !== 4 || maskParts.some((p) => isNaN(p))) return null;
      // Convert mask to 32-bit integer and count leading 1-bits
      const maskInt = ((maskParts[0] << 24) | (maskParts[1] << 16) | (maskParts[2] << 8) | maskParts[3]) >>> 0;
      let bits = 0;
      let m = maskInt;
      while (m & 0x80000000) {
        bits++;
        m = (m << 1) >>> 0;
      }
      // Calculate network address
      const ipParts = ip.split('.').map(Number);
      if (ipParts.length !== 4 || ipParts.some((p) => isNaN(p))) return null;
      const netParts = ipParts.map((p, i) => p & maskParts[i]);
      return `${netParts.join('.')}/${bits}`;
    } catch {
      return null;
    }
  };

  const handleChange = (field: keyof SuricataConfig, value: any) => {
    const newConfig = { ...config, [field]: value };
    setConfig(newConfig);
    setHasChanges(JSON.stringify(newConfig) !== JSON.stringify(originalConfig));
  };

  const toggleHomeNet = (subnet: string, checked: boolean) => {
    let newNets: string[];
    if (checked) {
      newNets = [...config.home_net, subnet];
    } else {
      newNets = config.home_net.filter((n) => n !== subnet);
    }
    handleChange('home_net', newNets);
  };

  const addCustomSubnet = () => {
    const trimmed = customSubnet.trim();
    if (!trimmed) return;
    // Basic CIDR validation
    if (!/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/.test(trimmed)) {
      toast.error('Invalid CIDR format (e.g. 10.0.0.0/8)');
      return;
    }
    if (config.home_net.includes(trimmed)) {
      toast.error('Network already in list');
      return;
    }
    handleChange('home_net', [...config.home_net, trimmed]);
    setCustomSubnet('');
  };

  const removeHomeNet = (subnet: string) => {
    handleChange('home_net', config.home_net.filter((n) => n !== subnet));
  };

  const handleSave = async () => {
    // Validate custom rules before saving
    const rulesValidation = validateCustomRules(config.custom_rules || '');
    if (!rulesValidation.valid) {
      toast.error(rulesValidation.error!);
      return;
    }

    try {
      setSaving(true);
      const response = await suricataService.updateConfig(edgeId, config);
      toast.success(response.message || 'Suricata configuration saved');
      setOriginalConfig(response.config);
      setHasChanges(false);
    } catch (error: any) {
      console.error('Failed to save Suricata config:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Feature flag gate
  if (!isFeatureEnabled('suricata')) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Suricata IDS/IPS</h3>
        </div>
        <FeatureDisabledBanner featureKey="suricata" featureName="Suricata IDS/IPS" />
      </div>
    );
  }

  const tabs = [
    { id: 'settings' as TabId, label: 'Settings', icon: Settings },
    { id: 'service-chain' as TabId, label: 'Service Chain', icon: Link2 },
  ];

  // Merge detected + existing home_net for the checklist
  const allNetworks = (() => {
    const seen = new Set<string>();
    const result: DetectedNetwork[] = [];

    // Add detected networks
    for (const dn of detectedNetworks) {
      if (!seen.has(dn.subnet)) {
        seen.add(dn.subnet);
        result.push(dn);
      }
    }

    // Add any existing home_net entries that weren't auto-detected
    for (const net of config.home_net) {
      if (!seen.has(net)) {
        seen.add(net);
        result.push({ subnet: net, source: `Custom — ${net}` });
      }
    }

    return result;
  })();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Shield className="w-5 h-5 text-primary-600" />
          <h3 className="text-lg font-semibold text-gray-900">Suricata IDS/IPS</h3>
        </div>
        <div className="flex items-center space-x-2">
          {config.enabled ? (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
              <CheckCircle className="w-3 h-3 mr-1" />
              {config.mode === 'ips' ? 'IPS Mode' : 'IDS Mode'}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              <AlertCircle className="w-3 h-3 mr-1" />
              Disabled
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Service Chain Tab (includes DSCP Mappings & Routing) */}
      {activeTab === 'service-chain' && <ServiceChainSettings edgeId={edgeId} />}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
        <div className="flex items-start">
          <Info className="w-5 h-5 text-blue-400 mr-2 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Intrusion Detection & Prevention System</p>
            <p className="mt-1">
              Suricata provides real-time network security monitoring. In <strong>IPS mode</strong>, malicious traffic
              is blocked inline. In <strong>IDS mode</strong>, threats are detected and logged but traffic is allowed.
            </p>
          </div>
        </div>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
        <div>
          <label className="font-medium text-gray-900">Enable Suricata</label>
          <p className="text-sm text-gray-500">Enable network intrusion detection/prevention</p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
        </label>
      </div>

      {config.enabled && (
        <div className="space-y-6">
          {/* Operation Mode */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Operation Mode</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label
                className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                  config.mode === 'ips' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="mode"
                  value="ips"
                  checked={config.mode === 'ips'}
                  onChange={() => handleChange('mode', 'ips')}
                  className="mt-1 text-primary-600 focus:ring-primary-500"
                />
                <div className="ml-3">
                  <span className="block font-medium text-gray-900">IPS Mode (Inline)</span>
                  <span className="block text-sm text-gray-500 mt-1">
                    Traffic passes through Suricata. Malicious packets are dropped in real-time.
                    Recommended for production environments.
                  </span>
                </div>
              </label>
              <label
                className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                  config.mode === 'ids' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="mode"
                  value="ids"
                  checked={config.mode === 'ids'}
                  onChange={() => handleChange('mode', 'ids')}
                  className="mt-1 text-primary-600 focus:ring-primary-500"
                />
                <div className="ml-3">
                  <span className="block font-medium text-gray-900">IDS Mode (Monitor)</span>
                  <span className="block text-sm text-gray-500 mt-1">
                    Traffic flows inline through the service chain but Suricata does not block packets.
                    Threats are detected and logged for monitoring.
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Detection Profile */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Detection Profile</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {(['low', 'medium', 'high', 'custom'] as const).map((profile) => (
                <label
                  key={profile}
                  className={`flex flex-col items-center p-4 border rounded-lg cursor-pointer transition-colors ${
                    config.detect_profile === profile
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="detect_profile"
                    value={profile}
                    checked={config.detect_profile === profile}
                    onChange={() => handleChange('detect_profile', profile)}
                    className="sr-only"
                  />
                  <span className="font-medium text-gray-900 capitalize">{profile}</span>
                  <span className="text-xs text-gray-500 mt-1 text-center">
                    {profile === 'low' && 'Minimal rules, low CPU'}
                    {profile === 'medium' && 'Balanced detection'}
                    {profile === 'high' && 'Maximum detection'}
                    {profile === 'custom' && 'User-defined rules — configure rule sources and custom rules below'}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Alert & Blocking Settings */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Alert & Blocking Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Alert Threshold</label>
                <select
                  value={config.alert_threshold}
                  onChange={(e) => handleChange('alert_threshold', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="all">All Alerts</option>
                  <option value="medium">Medium & Above</option>
                  <option value="high">High & Above</option>
                  <option value="critical">Critical Only</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Minimum severity level to generate alerts</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Block Severity (IPS Mode)</label>
                <select
                  value={config.block_severity}
                  onChange={(e) => handleChange('block_severity', e.target.value)}
                  className="input-base w-full"
                  disabled={config.mode !== 'ips'}
                >
                  <option value="none">Don't Block (Alert Only)</option>
                  <option value="critical">Critical Threats</option>
                  <option value="high">High & Critical</option>
                  <option value="medium">Medium & Above</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Minimum severity level to block traffic</p>
              </div>
            </div>
          </div>

          {/* Home Network */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Home Network (Protected Networks)</h4>
            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
              <div className="flex items-start">
                <Info className="w-4 h-4 text-blue-400 mr-2 mt-0.5" />
                <p className="text-xs text-blue-700">
                  HOME_NET defines which networks Suricata considers 'internal' for threat detection rules.
                  Auto-detected from your interface and routing configuration.
                </p>
              </div>
            </div>

            <div className="space-y-2">
              {allNetworks.map((net) => (
                <label
                  key={net.subnet}
                  className="flex items-center space-x-3 p-3 rounded hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={config.home_net.includes(net.subnet)}
                    onChange={(e) => toggleHomeNet(net.subnet, e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1 flex items-center justify-between">
                    <span className="text-sm font-mono text-gray-900">{net.subnet}</span>
                    <span className="text-xs text-gray-500">{net.source}</span>
                  </div>
                </label>
              ))}
            </div>

            {/* Selected networks as tags */}
            {config.home_net.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                {config.home_net.map((net) => (
                  <span
                    key={net}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                  >
                    {net}
                    <button
                      onClick={() => removeHomeNet(net)}
                      className="ml-1.5 text-primary-600 hover:text-primary-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Custom Network */}
            <div className="flex items-center space-x-2 mt-3">
              <input
                type="text"
                value={customSubnet}
                onChange={(e) => setCustomSubnet(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addCustomSubnet()}
                className="input-base flex-1"
                placeholder="Add custom network (e.g. 10.100.0.0/16)"
              />
              <button
                onClick={addCustomSubnet}
                className="btn-secondary inline-flex items-center px-3 py-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </button>
            </div>
          </div>

          {/* Rule Sources */}
          <div className="border-t pt-6">
            <h4 className="font-medium text-gray-900 mb-4">Rule Sources</h4>
            <div className="space-y-3">
              {[
                { id: 'et/open', name: 'Emerging Threats Open', desc: 'Free community rules' },
                { id: 'et/pro', name: 'Emerging Threats Pro', desc: 'Commercial rules (requires license)' },
                { id: 'oisf/trafficid', name: 'Traffic ID Rules', desc: 'Application identification rules' },
                { id: 'ptresearch/attackdetection', name: 'PT Research', desc: 'Advanced attack detection' },
              ].map((source) => (
                <label key={source.id} className="flex items-center space-x-3 p-3 rounded hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={config.rule_sources.includes(source.id)}
                    onChange={(e) => {
                      const current = config.rule_sources;
                      if (e.target.checked) {
                        handleChange('rule_sources', [...current, source.id]);
                      } else {
                        handleChange('rule_sources', current.filter((s) => s !== source.id));
                      }
                    }}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{source.name}</span>
                    <span className="block text-xs text-gray-500">{source.desc}</span>
                  </div>
                </label>
              ))}
            </div>

            {/* Auto-update toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mt-4">
              <div>
                <label className="font-medium text-gray-900">Auto-update Rules</label>
                <p className="text-sm text-gray-500">Automatically update rules daily</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={config.rule_update_enabled}
                  onChange={(e) => handleChange('rule_update_enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>

          {/* Advanced Settings Toggle */}
          <div className="border-t pt-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-primary-600 hover:text-primary-800 font-medium"
            >
              {showAdvanced ? 'Hide Advanced Settings' : 'Show Advanced Settings'}
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-6">
                {/* Performance Settings */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-4">Performance Settings</h5>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Threads</label>
                    <input
                      type="number"
                      value={config.threads}
                      onChange={(e) => handleChange('threads', parseInt(e.target.value) || 1)}
                      className="input-base w-full md:w-1/3"
                      min={1}
                      max={16}
                    />
                    <p className="text-xs text-gray-500 mt-1">Worker threads (auto = CPU cores)</p>
                  </div>
                </div>

                {/* Custom Rules */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">Custom Rules</h5>
                  <p className="text-xs text-gray-500 mb-3">
                    Enter Suricata rules in standard SID format (one per line). Each rule must include
                    an action (<code className="bg-gray-200 px-1 rounded">alert</code>, <code className="bg-gray-200 px-1 rounded">pass</code>, <code className="bg-gray-200 px-1 rounded">drop</code>, <code className="bg-gray-200 px-1 rounded">reject</code>)
                    and a <code className="bg-gray-200 px-1 rounded">sid:</code> field.
                  </p>
                  <textarea
                    value={config.custom_rules || ''}
                    onChange={(e) => handleChange('custom_rules', e.target.value)}
                    className="input-base w-full h-32 font-mono text-sm"
                    placeholder={'# Custom Suricata rules\nalert tcp any any -> $HOME_NET 22 (msg:"SSH connection attempt"; sid:1000001; rev:1;)\ndrop tcp any any -> $HOME_NET 445 (msg:"SMB exploit attempt"; sid:1000002; rev:1;)'}
                  />
                </div>
              </div>
            )}
          </div>

          {/* IPS Mode Warning */}
          {config.mode === 'ips' && config.block_severity !== 'none' && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-amber-400 mr-2 mt-0.5" />
                <div className="text-sm text-amber-700">
                  <p className="font-medium">IPS Mode Active - Traffic Will Be Blocked</p>
                  <p className="mt-1">
                    Suricata is configured to block traffic matching {config.block_severity} severity threats
                    and above. Ensure your rules are properly tuned to avoid false positives that could
                    disrupt legitimate traffic.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Button */}
      {canUpdate && (
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="btn-primary inline-flex items-center space-x-2 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span>{saving ? 'Saving...' : 'Save Configuration'}</span>
          </button>
        </div>
      )}

      {hasChanges && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <p className="text-sm text-amber-700">
            You have unsaved changes. Click "Save Configuration" to save, then sync the edge config
            for changes to take effect.
          </p>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default SuricataSettings;
